/**
 * DeviceProfileCache — localStorage-backed profile cache for fast login.
 */

export interface CachedProfile {
  account_id: string;
  username: string;
  avatar_url: string;
  account_type: 'parent' | 'child' | 'individual';
  family_id: string | null;
  last_used_at: number;
  trusted_on_this_device: boolean;
  trusted_until?: number;
}

const STORAGE_KEY = 'wbm:device-profiles';
const MAX_PROFILES = 12;
const INACTIVITY_EXPIRY_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

function readProfiles(): CachedProfile[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const profiles: CachedProfile[] = JSON.parse(raw);
    const now = Date.now();
    // Filter expired
    return profiles.filter(p => now - p.last_used_at < INACTIVITY_EXPIRY_MS);
  } catch {
    return [];
  }
}

function writeProfiles(profiles: CachedProfile[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
}

export const DeviceProfileCache = {
  list(): CachedProfile[] {
    return readProfiles().sort((a, b) => b.last_used_at - a.last_used_at);
  },

  upsert(p: CachedProfile): void {
    let profiles = readProfiles().filter(x => x.account_id !== p.account_id);
    profiles.unshift(p);
    // LRU eviction
    if (profiles.length > MAX_PROFILES) {
      profiles = profiles.slice(0, MAX_PROFILES);
    }
    writeProfiles(profiles);
  },

  remove(account_id: string): void {
    writeProfiles(readProfiles().filter(p => p.account_id !== account_id));
  },

  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY);
  },

  setTrusted(account_id: string, days: number): void {
    const profiles = readProfiles();
    const p = profiles.find(x => x.account_id === account_id);
    if (p) {
      p.trusted_on_this_device = true;
      p.trusted_until = Date.now() + days * 24 * 60 * 60 * 1000;
      writeProfiles(profiles);
    }
  },

  revokeTrusted(account_id: string): void {
    const profiles = readProfiles();
    const p = profiles.find(x => x.account_id === account_id);
    if (p) {
      p.trusted_on_this_device = false;
      p.trusted_until = undefined;
      writeProfiles(profiles);
    }
  },

  getMostRecent(): CachedProfile | null {
    const profiles = this.list();
    return profiles[0] ?? null;
  },

  getFamilyProfiles(family_id: string): CachedProfile[] {
    return this.list().filter(p => p.family_id === family_id);
  },
};
